import { icons } from '../icons';
import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';

type KoobSlideOverProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  title?: React.ReactNode;
  widthClass?: string;
  /** Bottom bar, outside the scroll area (actions). */
  footer?: React.ReactNode;
  children?: React.ReactNode;
};

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

// A panel that slides in from the right edge, for detail that belongs next
// to a list rather than on top of it. Same dialog plumbing as KoobModal.
export default function KoobSlideOver({
  open,
  setOpen,
  title,
  widthClass = 'max-w-md',
  footer,
  children,
}: KoobSlideOverProps) {
  return (
    // Portaled, but React still bubbles the slide-over's clicks to whatever
    // rendered it (a clickable card). The stop cannot live inside the Dialog:
    // on an outside click Headless UI closes it from a document listener, and
    // React 17 unmounts it before handling that same click, so React starts
    // from the nearest mounted ancestor and skips every handler inside. This
    // span stays mounted; `contents` keeps it out of the opener's layout.
    <span className="contents" onClick={stopPropagation}>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-modal" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-y-0 right-0 flex max-w-full">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in duration-200"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel
                className={`flex h-full w-screen ${widthClass} flex-col bg-white text-left shadow-xl`}
              >
                <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-4 py-5 sm:px-6">
                  {/* A div: a title may carry its own heading and badges. */}
                  <Dialog.Title
                    as="div"
                    className="min-w-0 flex-1 text-xl font-semibold text-gray-500"
                  >
                    {title}
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label="Close"
                    className="border-none bg-transparent cursor-pointer rounded text-gray-400 outline-none hover:text-gray-600 focus-visible:ring-2 focus-visible:ring-brand"
                  >
                    <i className={icons.close} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
                  {children}
                </div>
                {footer && (
                  <div className="border-x-0 border-b-0 border-t border-solid border-gray-200 bg-gray-50 px-4 py-3 sm:px-6">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </span>
  );
}
